import {
  ProgramReward,
  ProgramRewardPeriod,
} from '@/types/program-rewards/program-rewards'
import {
  ProgramRewardsByCategory,
  ProgramRewardsDeviceAmount,
} from '@/types/programs-new/programs-new'
import { match } from 'ts-pattern'

export function getRewardAmounts(reward: ProgramReward) {
  return match(reward.type)
    .with('charge_incentive', () => ({
      dollarAmount:
        reward.charge_incentive_program?.reward_currency_per_kwh ?? 0,
      dollarAmountYearly: null,
    }))
    .with('flat_rate', () => ({
      dollarAmountYearly: calculateYearlyAmount(reward.amount, reward.period),
      dollarAmount: null,
    }))
    .with('referral', () => ({
      dollarAmount: reward.amount / 100,
      dollarAmountYearly: null,
    }))
    .with('sign_up', () => ({
      dollarAmount: reward.amount / 100,
      dollarAmountYearly: null,
    }))
    .with('end_of_program', () => ({
      dollarAmount: reward.amount / 100,
      dollarAmountYearly: null,
    }))
    .otherwise(() => ({
      dollarAmount: null,
      dollarAmountYearly: null,
    }))
}

export function calculateYearlyAmount(
  amount: number,
  period: ProgramRewardPeriod,
): number {
  const dollars = amount / 100
  return match(period)
    .with(ProgramRewardPeriod.DAILY, () => dollars * 365)
    .with(ProgramRewardPeriod.WEEKLY, () => dollars * 52)
    .with(ProgramRewardPeriod.MONTHLY, () => dollars * 12)
    .with(ProgramRewardPeriod.QUARTERLY, () => dollars * 4)
    .with(ProgramRewardPeriod.YEARLY, () => dollars)
    .with(ProgramRewardPeriod.ONE_TIME, () => dollars)
    .with(ProgramRewardPeriod.UNSCHEDULED, () => dollars)
    .otherwise(() => 0)
}

export function buildRewardsByCategory(
  rewards: ProgramReward[],
): ProgramRewardsByCategory {
  return rewards.reduce<ProgramRewardsByCategory>((acc, reward) => {
    const deviceTypeAmounts: ProgramRewardsDeviceAmount[] =
      reward.eligible_device_types.map((deviceType) => ({
        deviceType:
          deviceType.toLowerCase() as ProgramRewardsDeviceAmount['deviceType'],
        ...getRewardAmounts(reward),
      }))

    return match({
      type: reward.type,
      earnBeforeVerification: reward.earn_before_verification,
    })
      .with({ type: 'sign_up', earnBeforeVerification: false }, () => ({
        ...acc,
        enrollment: [...(acc.enrollment ?? []), ...deviceTypeAmounts],
      }))
      .with({ type: 'sign_up', earnBeforeVerification: true }, () => ({
        ...acc,
        application: [...(acc.application ?? []), ...deviceTypeAmounts],
      }))
      .with({ type: 'referral' }, () => ({
        ...acc,
        referral: [...(acc.referral ?? []), ...deviceTypeAmounts],
      }))
      .with({ type: 'flat_rate' }, () => ({
        ...acc,
        participation: [...(acc.participation ?? []), ...deviceTypeAmounts],
      }))
      .with({ type: 'charge_incentive' }, () => ({
        ...acc,
        offPeakCharging: [...(acc.offPeakCharging ?? []), ...deviceTypeAmounts],
      }))
      .with({ type: 'end_of_program' }, () => ({
        ...acc,
        endOfProgram: [...(acc.endOfProgram ?? []), ...deviceTypeAmounts],
      }))
      .otherwise(() => acc)
  }, {})
}

export function computeTotalRewardsDollars(
  rewardsByCategory: ProgramRewardsByCategory | null,
): number {
  if (!rewardsByCategory) return 0
  let total = 0
  for (const amounts of Object.values(rewardsByCategory)) {
    if (!amounts) continue
    for (const a of amounts) {
      total += a.dollarAmountYearly ?? a.dollarAmount ?? 0
    }
  }
  return Math.floor(total)
}
